import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { Spinner, Alert } from "flowbite-react";
import { ArrowLeft, Download, School, Calendar, Sparkles, Star } from "lucide-react";
import AppHeader from "./AppHeader";
import { Therapist } from "../types/therapist";
import PublicSessionsList, { PublicAppointment } from "./PublicSessionsList";
import { useAuth } from "./AuthContext";
import { goToApp } from "../utils/crossDomainNav";
import { scrollWindowToTopSoon } from "./ScrollToTop";

const getInitials = (name: string): string => {
  if (!name) return "TT";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const formatGraduationDate = (date: Date | null): string => {
  if (!date) return "Data não informada";

  try {
    const year = date.getFullYear();
    return `Concluído em ${year}`;
  } catch (error) {
    return "Data não informada";
  }
};

const formatDurationMinutes = (minutes?: number | null): string => {
  if (!minutes && minutes !== 0) return "Duração não informada";
  if (minutes >= 60 && minutes % 60 === 0) return `${minutes / 60}h`;
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `${minutes} minutos`;
};

const TherapistShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [therapist, setTherapist] = React.useState<Therapist | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [appointments, setAppointments] = React.useState<PublicAppointment[]>([]);
  const [appointmentsLoading, setAppointmentsLoading] = React.useState(true);
  const [schedulingAppointment, setSchedulingAppointment] = React.useState<string | null>(null);
  const [schedulingError, setSchedulingError] = React.useState<{ [key: string]: string }>({});
  const [schedulingSuccess, setSchedulingSuccess] = React.useState<{ [key: string]: boolean }>({});
  const [userParticipations, setUserParticipations] = React.useState<Set<string>>(new Set());
  const [participationCredits, setParticipationCredits] = React.useState<number>(0);

  React.useEffect(() => {
    scrollWindowToTopSoon();

    const fetchTherapist = async () => {
      if (!id) {
        setError("ID do terapeuta não fornecido.");
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        let therapistDoc = doc(db, "therapists", id);
        let therapistSnapshot = await getDoc(therapistDoc);
        if (!therapistSnapshot.exists()) {
          therapistDoc = doc(db, "users", id);
          therapistSnapshot = await getDoc(therapistDoc);
        }
        if (therapistSnapshot.exists()) {
          setTherapist({
            id: therapistSnapshot.id,
            ...(therapistSnapshot.data() as Omit<Therapist, "id">),
          });
        } else {
          setError("Terapeuta não encontrado.");
        }
      } catch (err: any) {
        setError(err?.message || "Erro ao carregar terapeuta.");
      } finally {
        setIsLoading(false);
        // Depois do conteúdo montar — evita ficar “preso” no scroll da lista
        scrollWindowToTopSoon();
      }
    };
    fetchTherapist();
  }, [id]);

  React.useEffect(() => {
    if (!id) return;

    const fetchAppointments = async () => {
      setAppointmentsLoading(true);
      try {
        const now = new Date();
        const baseFilter = [where("dataSessao", ">=", now), where("status", "==", "confirmado")];
        const appointmentsQuery1 = query(
          collection(db, "appointments"),
          where("terapeutaId", "==", id),
          where("isPublic", "==", true),
          ...baseFilter
        );

        const appointmentsQuery2 = query(
          collection(db, "appointments"),
          where("terapeutaId", "==", id),
          where("permiteParticipantes", "==", true),
          ...baseFilter
        );

        const [appointmentsSnapshot1, appointmentsSnapshot2] = await Promise.all([
          getDocs(appointmentsQuery1),
          getDocs(appointmentsQuery2),
        ]);

        const appointmentsMap = new Map();

        const appointmentPromises = [...appointmentsSnapshot1.docs, ...appointmentsSnapshot2.docs].map(async (doc) => {
          if (!appointmentsMap.has(doc.id)) {
            const data = doc.data();
            let scheduleDate = data.dataSessao.toDate();
            if (!scheduleDate) {
              scheduleDate = new Date(data.dataAgendamento);
              const scheduleTime = data.horarioAgendamento?.split(":") || ["00", "00"];
              scheduleDate.setHours(parseInt(scheduleTime[0], 10));
              scheduleDate.setMinutes(parseInt(scheduleTime[1], 10));
            }

            let participantCount = data.participantCount;
            if (typeof participantCount !== "number") {
              try {
                const participantsSnap = await getDocs(collection(db, "appointments", doc.id, "participants"));
                participantCount = participantsSnap.size;
              } catch (error) {
                console.error(`Erro ao contar participantes para ${doc.id}:`, error);
                participantCount = 0;
              }
            }

            appointmentsMap.set(doc.id, {
              id: doc.id,
              therapistName: data.terapeutaNomePublico || "",
              therapistId: data.terapeutaId,
              patientName: data.pacienteNome,
              patientId: data.pacienteId,
              description: data.description,
              date: scheduleDate,
              duration: data.sessionDuration || 60,
              status: data.status === "confirmado" ? ("scheduled" as const) : (data.status as "scheduled" | "in-progress" | "completed"),
              roomId: data.roomId,
              public: data.isPublic || data.permiteParticipantes,
              expectedParticipants: participantCount,
            });
          }
        });

        await Promise.all(appointmentPromises);

        const appointmentsList = Array.from(appointmentsMap.values());
        setAppointments(appointmentsList);
      } catch (err: any) {
        console.error("Erro ao buscar appointments:", err);
      } finally {
        setAppointmentsLoading(false);
      }
    };

    fetchAppointments();
  }, [id]);

  React.useEffect(() => {
    if (!user?.uid) {
      setUserParticipations(new Set());
      setParticipationCredits(0);
      return;
    }

    const fetchUserData = async () => {
      try {
        // Fetch user credit balance
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const participationCreds = userData?.creditBalances?.sessions?.participation || 0;
          setParticipationCredits(participationCreds);
        }

        // Fetch user participations
        const appointmentsSnapshot = await getDocs(query(collection(db, "appointments"), where("permiteParticipantes", "==", true)));

        const participationChecks = await Promise.all(
          appointmentsSnapshot.docs.map(async (appointmentDoc) => {
            const participantDoc = await getDocs(
              query(collection(db, "appointments", appointmentDoc.id, "participants"), where("userId", "==", user.uid))
            );
            return { appointmentId: appointmentDoc.id, isParticipant: !participantDoc.empty };
          })
        );

        const participations = new Set(participationChecks.filter((p) => p.isParticipant).map((p) => p.appointmentId));
        setUserParticipations(participations);
      } catch (error) {
        console.error("Erro ao buscar dados do usuário:", error);
      }
    };

    fetchUserData();
  }, [user?.uid]);

  const handleScheduleParticipation = async (appointmentId: string) => {
    if (!user?.uid) {
      setSchedulingError((prev) => ({ ...prev, [appointmentId]: "Você precisa estar logado para agendar participação." }));
      return;
    }

    if (userParticipations.has(appointmentId)) {
      setSchedulingError((prev) => ({ ...prev, [appointmentId]: "Você já está registrado nesta sessão." }));
      return;
    }

    setSchedulingAppointment(appointmentId);
    setSchedulingError((prev) => ({ ...prev, [appointmentId]: "" }));
    setSchedulingSuccess((prev) => ({ ...prev, [appointmentId]: false }));

    try {
      const consumeTicket = httpsCallable(functions, "consumeRepresentativeTicket");
      await consumeTicket({ appointmentId });

      setSchedulingSuccess((prev) => ({ ...prev, [appointmentId]: true }));
      setUserParticipations((prev) => new Set(prev).add(appointmentId));

      setTimeout(() => {
        setSchedulingSuccess((prev) => ({ ...prev, [appointmentId]: false }));
      }, 3000);
    } catch (error: any) {
      console.error("Erro ao agendar participação:", error);
      const errorMessage = error.message || "Erro ao agendar participação. Tente novamente.";
      setSchedulingError((prev) => ({ ...prev, [appointmentId]: errorMessage }));
    } finally {
      setSchedulingAppointment(null);
    }
  };

  const handleScheduleConstellation = () => {
    // Agendamento é público: dia/horário sem login; conta/e-mail ficam no checkout.
    if (!therapist?.id) {
      return;
    }
    goToApp(
      `/agendamento?plan=participante-constelar&therapistId=${encodeURIComponent(therapist.id)}`,
      navigate,
    );
  };

  // Telas de Loading e Erro originais
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#EAF4F2]">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#EAF4F2] min-h-screen">
        <AppHeader />
        <div className="container mx-auto max-w-2xl p-4 pt-24 sm:pt-28">
          <Alert color="failure">{error}</Alert>
          <Link
            to="/terapeutas"
            className="mt-4 w-fit inline-flex items-center gap-2 bg-[#4A7C75] text-white px-5 py-2 rounded-full font-semibold text-sm hover:bg-[#406b65] transition-colors">
            <ArrowLeft className="mr-2 h-5 w-5" /> Voltar
          </Link>
        </div>
      </div>
    );
  }

  if (!therapist) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-[#EAF4F2]">
        <p style={{ color: "#4A7C75" }}>Terapeuta não encontrado.</p>
      </div>
    );
  }

  // Renderização principal
  return (
    <div className="bg-[#EAF4F2] min-h-screen">
      <AppHeader />

      <div className="container mx-auto max-w-6xl p-4 pt-24 pb-8 sm:pt-28 sm:pb-12">
        <Link
          to="/terapeutas"
          className="mb-6 w-fit inline-flex items-center gap-2 bg-[#4A7C75] text-white px-5 py-2 rounded-full font-semibold text-sm hover:bg-[#406b65] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4A7C75] transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Link>

        <div className="lg:grid lg:gap-8">
          {/* Coluna Esquerda: Perfil (Original) */}
          <div className="space-y-8">
            <div className="p-6 bg-white rounded-2xl shadow-md">
              <div className="flex flex-col items-center text-center sm:flex-row sm:text-left sm:items-start sm:gap-6">
                <div
                  className="mb-4 sm:mb-0 flex-shrink-0 
                             w-36 h-36 sm:w-40 sm:h-40 lg:w-48 lg:h-48
                             rounded-full 
                             bg-gray-200 flex items-center justify-center 
                             text-4xl sm:text-5xl lg:text-6xl font-bold 
                             text-gray-600 border-2 border-gray-300 overflow-hidden">
                  {therapist.profilePhotoURL ? (
                    <img src={therapist.profilePhotoURL} alt={`Foto de ${therapist.nomePublico}`} className="w-full h-full object-cover" />
                  ) : (
                    getInitials(therapist.nomePublico)
                  )}
                </div>

                <div className="w-full">
                  <h1 className="text-3xl font-bold" style={{ color: "#6C4E78" }}>
                    {therapist.nomePublico}
                  </h1>
                  <span className="text-lg font-medium mt-1 block" style={{ color: "#7ED99C" }}>
                    {therapist.title}
                  </span>
                  <span className="text-md mb-6 block opacity-80" style={{ color: "#4A7C75" }}>
                    {therapist.especialidade}
                  </span>

                  <h2 className="text-2xl font-semibold mb-4 text-left w-full border-t pt-4 mt-4" style={{ color: "#6C4E78" }}>
                    Como posso te ajudar?
                  </h2>
                  <p className="whitespace-pre-line text-left w-full" style={{ color: "#4A7C75" }}>
                    {therapist.bio}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Perfil → logo abaixo: Agendar constelação (como antes) */}
        {(therapist.horariosDisponiveis && Object.keys(therapist.horariosDisponiveis).length > 0) || therapist.sessionDuration ? (
          <div className="mt-8 p-6 bg-white rounded-2xl shadow-md">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2" style={{ color: "#6C4E78" }}>
              <Calendar className="w-6 h-6" style={{ color: "#7ED99C" }} />
              Informações de Atendimento
            </h2>

            <div className="space-y-8">
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-6 border border-teal-100">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <Calendar className="w-6 h-6" style={{ color: "#4A7C75" }} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold" style={{ color: "#6C4E78" }}>
                        Duração da Sessão
                      </h3>
                      <p className="text-2xl font-bold" style={{ color: "#4A7C75" }}>
                        {formatDurationMinutes((therapist as any)?.sessionDuration)}
                      </p>
                    </div>
                  </div>

                  <div className="text-center lg:text-right">
                    <p className="text-sm mb-3" style={{ color: "#4A7C75" }}>
                      Agende uma sessão exclusiva com {therapist.nomePublico.split(" ")[0]}
                    </p>
                    <button
                      onClick={handleScheduleConstellation}
                      className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-full font-semibold text-lg hover:bg-teal-700 transition-colors shadow-md">
                      Agendar constelação
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4" style={{ color: "#6C4E78" }}>
                  Horários de Atendimento
                </h3>
                {therapist.horariosDisponiveis && Object.keys(therapist.horariosDisponiveis).length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((key) => {
                      const labelMap: any = {
                        monday: "Segunda-feira",
                        tuesday: "Terça-feira",
                        wednesday: "Quarta-feira",
                        thursday: "Quinta-feira",
                        friday: "Sexta-feira",
                        saturday: "Sábado",
                        sunday: "Domingo",
                      };
                      const slots = (therapist.horariosDisponiveis as any)?.[key];
                      const hasSlots = Array.isArray(slots) && slots.length > 0;

                      return (
                        <div
                          key={key}
                          className={`p-4 rounded-lg border ${hasSlots ? "bg-gray-50 border-gray-200" : "bg-gray-25 border-gray-100"}`}>
                          <div className="font-medium text-sm mb-2" style={{ color: "#6C4E78" }}>
                            {labelMap[key]}
                          </div>
                          <div className="space-y-1">
                            {hasSlots ? (
                              slots.map((s: any, i: number) => (
                                <div
                                  key={i}
                                  className="bg-white px-3 py-2 rounded-md border border-gray-100 text-sm font-medium"
                                  style={{ color: "#4A7C75" }}>
                                  {s.inicio || "--:--"} às {s.fim || "--:--"}
                                </div>
                              ))
                            ) : (
                              <div className="text-xs text-gray-400 italic">Não disponível</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-lg font-medium">Horários não informados</p>
                    <p className="text-sm">Entre em contato para verificar disponibilidade</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Formações */}
        {therapist.certificates && therapist.certificates.length > 0 && (
          <div className="mt-8 p-6 bg-white rounded-2xl shadow-md">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2" style={{ color: "#6C4E78" }}>
              <School className="w-6 h-6" style={{ color: "#7ED99C" }} />
              Formações
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {therapist.certificates.map((certificate, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-lg font-semibold mb-2" style={{ color: "#6C4E78" }}>
                    {certificate.name || "Formação não informada"}
                  </h3>
                  <p className="text-sm mb-1" style={{ color: "#4A7C75" }}>
                    {certificate.institution || "Instituição não informada"}
                  </p>
                  <p className="text-xs mb-3 opacity-80" style={{ color: "#4A7C75" }}>
                    {formatGraduationDate(certificate.graduationDate)}
                  </p>
                  {certificate.certificateURL && (
                    <a
                      href={certificate.certificateURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-xs hover:underline"
                      style={{ color: "#4A7C75" }}>
                      <Download className="w-3 h-3" />
                      Ver Certificado
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Participação em sessões públicas — no final, para não confundir com agendamento */}
        {appointments.length > 0 && (
          <div className="mt-8 p-6 bg-gradient-to-br from-[#F3F6F1] via-[#F8FAF7] to-[#F0F4ED] rounded-2xl shadow-md relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <Sparkles className="w-6 h-6 text-[#A56096]/30 animate-pulse" />
            </div>
            <div className="absolute top-8 left-8">
              <Star className="w-5 h-5 text-[#3A5859]/25 animate-pulse delay-500" />
            </div>

            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center bg-white/70 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg border border-white/20">
                <div className="p-2 bg-gradient-to-r from-[#3A5859] to-[#A56096] rounded-full mr-3">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-[#3A5859]">Sessões Públicas Disponíveis</h2>
                <Star className="w-5 h-5 ml-3 text-[#A56096] animate-pulse" />
              </div>
            </div>

            <p className="text-center text-gray-600 mb-6 max-w-2xl mx-auto">
              Participe como observador ou representante nas sessões públicas de {therapist.nomePublico.split(" ")[0]}
            </p>

            <PublicSessionsList
              appointments={appointments}
              isLoading={appointmentsLoading}
              userParticipations={userParticipations}
              schedulingAppointment={schedulingAppointment}
              schedulingError={schedulingError}
              schedulingSuccess={schedulingSuccess}
              onScheduleParticipation={handleScheduleParticipation}
              emptyMessage={`${therapist.nomePublico.split(" ")[0]} não possui sessões públicas agendadas no momento.`}
              currentUserId={user?.uid}
              participationCredits={participationCredits}
              onViewPlans={() => navigate("/planos")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TherapistShow;
